<script setup lang="ts">
import { ref, onMounted, onUnmounted, watch, nextTick } from 'vue'
import axios from 'axios'
import { marked } from 'marked'
import { useVersion } from '../composables/useVersion'

// Component properties
const props = defineProps({
  show: Boolean,
})

// Emit events
const emit = defineEmits(['close'])

// Get the app version
const { version: appVersion } = useVersion()

// State 
const releaseNotes = ref<string>('')
const loading = ref(true)
const error = ref(false)
const modalRef = ref<HTMLDivElement | null>(null)
const closeButtonRef = ref<HTMLButtonElement | null>(null)
const lastActiveElement = ref<HTMLElement | null>(null)

// Load release notes from CHANGELOG.md
onMounted(async () => {
  try {
    loading.value = true
    // Fetch the CHANGELOG.md file
    const response = await axios.get('/CHANGELOG.md')
    releaseNotes.value = marked(response.data) as string
  } catch (err) {
    console.error('Failed to load release notes:', err)
    error.value = true
    releaseNotes.value = 'Failed to load release notes. Please try again later.'
  } finally {
    loading.value = false
  }
})

// Method to close the modal
function closeModal() {
  emit('close')
  
  // Return focus to the element that was active before the modal was opened
  if (lastActiveElement.value) {
    nextTick(() => {
      lastActiveElement.value?.focus()
    })
  }
}

// Handle escape key press
function handleKeyDown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    closeModal()
  }
}

// Trap focus within modal for keyboard navigation
function trapFocus(event: KeyboardEvent) {
  if (!modalRef.value || event.key !== 'Tab') return
  
  const focusableElements = modalRef.value.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )
  
  if (focusableElements.length === 0) return
  
  const firstElement = focusableElements[0] as HTMLElement
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement
  
  // Shift+Tab on first element should wrap to last element
  if (event.shiftKey && document.activeElement === firstElement) {
    lastElement.focus()
    event.preventDefault()
  } 
  // Tab on last element should wrap to first element
  else if (!event.shiftKey && document.activeElement === lastElement) {
    firstElement.focus()
    event.preventDefault()
  }
}

// Watch for modal visibility changes
watch(() => props.show, (newVal) => {
  if (newVal) {
    // Store the currently focused element
    lastActiveElement.value = document.activeElement as HTMLElement
    
    // Add event listeners when modal opens
    document.addEventListener('keydown', handleKeyDown)
    
    // Focus the close button when modal opens
    nextTick(() => {
      if (closeButtonRef.value) {
        closeButtonRef.value.focus()
      }
    })
  } else {
    // Remove event listeners when modal closes
    document.removeEventListener('keydown', handleKeyDown)
  }
})

// Add event listeners when component is mounted
onMounted(() => {
  if (props.show) {
    document.addEventListener('keydown', handleKeyDown)
  }
})

// Remove event listeners when component is unmounted
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <div v-if="show" class="release-notes-modal" @keydown="trapFocus">
    <div class="modal-overlay" @click="closeModal"></div>
    <div 
      ref="modalRef"
      class="modal-container" 
      role="dialog" 
      aria-modal="true"
      aria-labelledby="release-notes-title"
    >
      <div class="modal-header">
        <h2 id="release-notes-title">Release Notes <span class="version-tag">v{{ appVersion }}</span></h2>
        <button 
          ref="closeButtonRef"
          class="close-button" 
          @click="closeModal"
          aria-label="Close release notes"
        >
          ×
        </button>
      </div>
      <div class="modal-content">
        <div v-if="loading" class="loading-spinner" aria-live="polite">
          <div class="spinner" aria-hidden="true"></div>
          <p>Loading release notes...</p>
        </div>
        <div v-else-if="error" class="error-message" aria-live="assertive">
          Failed to load release notes. Please try again later.
        </div>
        <div v-else class="release-notes-content" v-html="releaseNotes"></div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.release-notes-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.75);
  animation: fadeIn 0.2s ease-out;
}

.modal-container {
  position: relative;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3);
  animation: slideIn 0.3s ease-out;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  background-color: #f7f7f7;
  border-bottom: 1px solid #e0e0e0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #333;
}

.version-tag {
  display: inline-block;
  margin-left: 10px;
  font-size: 0.85rem;
  font-weight: normal;
  color: white;
  background-color: #0084ff;
  padding: 2px 8px;
  border-radius: 20px;
}

.close-button {
  background: none;
  border: none;
  font-size: 24px;
  line-height: 24px;
  cursor: pointer;
  color: #555;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: #e0e0e0;
  color: #333;
}

.close-button:focus {
  outline: 2px solid #0084ff;
  outline-offset: 2px;
  box-shadow: 0 0 0 4px rgba(0, 132, 255, 0.3);
}

.modal-content {
  padding: 20px;
  overflow-y: auto;
  flex-grow: 1;
  color: #333;
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #0084ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

.error-message {
  text-align: center;
  color: #e53e3e;
  padding: 20px;
}

.release-notes-content {
  line-height: 1.6;
}

.release-notes-content h1 {
  font-size: 1.5rem;
  margin-top: 0;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}

.release-notes-content h2 {
  font-size: 1.25rem;
  margin-top: 24px;
  margin-bottom: 12px;
}

.release-notes-content h3 {
  font-size: 1.1rem;
  margin-top: 20px;
  margin-bottom: 10px;
}

.release-notes-content ul {
  padding-left: 20px;
}

.release-notes-content li {
  margin-bottom: 6px;
}

/* Animations */
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

@keyframes slideIn {
  0% {
    transform: translateY(20px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    max-height: 90vh;
  }
  
  .modal-header {
    padding: 12px 16px;
  }
  
  .modal-header h2 {
    font-size: 1.1rem;
  }
  
  .modal-content {
    padding: 16px;
  }
}
</style>